import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import { CREATE_SURVEY, UPDATE_SURVEY } from '../../constants';
import { getSurveyItem, swapItems } from '../../helpers/surveyUtil';
import { ChapterProps, SectionProps } from '../../models/global';
import TextField from '../FormComponents/TextField';
import FieldBlock from './FieldBlock';
import { InterrelatedFieldRef } from './InterrelatedField';

interface SectionComponentProps {
  section: SectionProps;
  chapter: ChapterProps;
  chapters: ChapterProps[];
  sectionIndex: number;
  language: string;
  modalRef: React.MutableRefObject<InterrelatedFieldRef>;
  invalidFields: string[];
  readonly?: boolean;
  type: typeof CREATE_SURVEY | typeof UPDATE_SURVEY;
  forceToUpdate: () => void;
}

const Section = React.memo(
  ({
    section,
    chapter,
    sectionIndex,
    language,
    modalRef,
    invalidFields,
    chapters,
    readonly,
    type,
    forceToUpdate,
  }: SectionComponentProps) => {
    const [name, setName] = useState('');
    const [isFromNewPage, setIsFromNewPage] = useState(false);

    useEffect(() => {
      setIsFromNewPage(section.isFromNewPage);
    }, [section.isFromNewPage]);

    useEffect(() => {
      setName(language === 'English' ? section.name : `${section[`name${language}`] || ''}`);
    }, [section]);

    const handleSectionTitle = (value: string, chapterId: string, sectionId: string) => {
      const section = getSurveyItem(chapters, chapterId, sectionId) as SectionProps;
      const key = language === 'English' ? 'name' : `name${language}`;
      section[key] = value;
      setName(value);
    };

    const handleSectionNewPage = (chapterId: string, sectionId: string) => {
      const section = getSurveyItem(chapters, chapterId, sectionId) as SectionProps;
      section.isFromNewPage = !section.isFromNewPage;
      setIsFromNewPage(section.isFromNewPage);
    };

    const swapSections = (chapterId: string, sectionIndex: number, direction: 'prev' | 'next') => {
      const chapter = getSurveyItem(chapters, chapterId) as ChapterProps;
      swapItems(chapter.sections, sectionIndex, direction);
      forceToUpdate();
    };

    const removeSection = (chapterId: string, sectionId: string) => {
      const chapter = getSurveyItem(chapters, chapterId) as ChapterProps;
      chapter.sections = chapter.sections.filter(el => el.id !== sectionId);
      let order = 1;
      chapter.sections.forEach(section => {
        section.order = order;
        order++;
      });
      forceToUpdate();
    };

    return (
      !!section && (
        <Box key={section.id}>
          <Box className='page-break' style={isFromNewPage ? { opacity: 1 } : {}}>
            <Box className='line' />
            <Tooltip title='Page break' sx={{ pr: 0.5 }}>
              <Checkbox onClick={() => handleSectionNewPage(chapter.id, section.id)} checked={isFromNewPage} />
            </Tooltip>
            <Typography display='inline'>Page break</Typography>
            <Box className='line' sx={{ ml: 1 }} />
          </Box>
          <Card variant='outlined' className='section-container' id={section.id}>
            <CardContent>
              <Box sx={{ mb: 2.5, width: '100%' }} display='flex'>
                <Box sx={{ width: '80%' }}>
                  {type === UPDATE_SURVEY && (
                    <Typography display='inline' fontWeight={700}>
                      {chapter.order}.{section.order}
                    </Typography>
                  )}
                  <TextField
                    id='section-name'
                    variant='standard'
                    className='name-field'
                    value={name}
                    onChange={e => handleSectionTitle(e.target.value, chapter.id, section.id)}
                    error={invalidFields.includes(`SECTION_${section.id}`) && language === 'English'}
                    required
                    placeholder='Section name'
                    maxLength={600}
                  />
                  {language !== 'English' && (
                    <Typography className='english-value'>English: {section.name || 'N/A'}</Typography>
                  )}
                </Box>
                <Box sx={{ mt: 0.25, ml: 0.5 }}>
                  {sectionIndex !== 0 && (
                    <RemoveCircleIcon htmlColor='red' onClick={() => removeSection(chapter.id, section.id)} />
                  )}
                  {sectionIndex !== chapter.sections.length - 1 && (
                    <Tooltip title='Swap with the next one'>
                      <ArrowDownwardIcon
                        fontSize='small'
                        sx={{ mb: 0.25 }}
                        onClick={() => swapSections(chapter.id, sectionIndex, 'next')}
                      />
                    </Tooltip>
                  )}
                  {sectionIndex !== 0 && (
                    <Tooltip title='Swap with the previous one'>
                      <ArrowUpwardIcon
                        fontSize='small'
                        sx={{ mb: 0.25 }}
                        onClick={() => swapSections(chapter.id, sectionIndex, 'prev')}
                      />
                    </Tooltip>
                  )}
                </Box>
              </Box>
              {section.fieldBlocks.map((block, index) => (
                <FieldBlock
                  key={index}
                  block={block}
                  section={section}
                  chapter={chapter}
                  index={index}
                  language={language}
                  modalRef={modalRef}
                  invalidFields={invalidFields}
                  chapters={chapters}
                  readonly={readonly}
                  type={type}
                  forceToUpdate={forceToUpdate}
                />
              ))}
            </CardContent>
          </Card>
        </Box>
      )
    );
  },
);

Section.displayName = 'Section';

export default Section;
